import { Transition } from '@uirouter/core';
import { TransitionService } from '@uirouter/angular';
import { Store } from '@ngrx/store';
import { AuthService } from '@core/state/auth/auth.service';
import { take, withLatestFrom } from 'rxjs';
import { environment } from '@environments/environment';
import { RouterService } from '@core/state/router/router.service';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { SessionService } from '@core/state/session/session.service';
import { getWindow } from '@vendor/window/window';
import { AccountRole } from '@core/models/account/account-role';
import { STORE_LOGIN_PREFS } from '@core/local-storage/local-storage';
import { IAppRouterState } from '@core/state/router/router-state';
import { AuthSession } from '@core/models/auth/auth-session';
import { SetRedirectStateAction } from '@core/state/session/session-actions';
import { IAppState } from '@core/state/app/app-state';
import {
    DEVSUPPORT_DASHBOARD,
    LOGIN,
    LOGOUT,
    PASSWORD_RESET,
    PASSWORD_VALIDATION_REQUEST,
    PASSWORD_VALIDATION_SUBMIT
} from '@core/router/app-states';
import { RouteType } from '../route-type';

/**
 * onBefore transition hook to validate Public/Private routes and authenticate permissions/roles.
 * @param transitionService
 */
export function validRouteHook(transitionService: TransitionService) {

    /**
     * onStart event handler. Matching all states.
     */
    transitionService.onBefore({}, processTransition, { priority: 20 });

    function processTransition(transition: Transition): any {
        const authService: AuthService = transition
            .injector()
            .get<AuthService>(AuthService);
        const routerService: RouterService = transition
            .injector()
            .get<RouterService>(RouterService);
        const localStorageService: LocalStorageService = transition.injector().get<LocalStorageService>(LocalStorageService);
        const sessionService: SessionService = transition
            .injector()
            .get<SessionService>(SessionService);
        const store$: Store<IAppState> = transition
            .injector()
            .get<Store<IAppState>>(Store);
        const toState = transition.to();
        const w: any = getWindow();

        let path: string =
            transition.router.stateService.href(
                toState.name,
                {},
                { absolute: true }
            ) || w.location.href;
        path = extractAnchorWithParams(path);
        path = removeQuery(path);

        return new Promise((resolve, reject) => {
            
            authService.isAuthenticated()
                .pipe(
                    take(1),
                    withLatestFrom(
                        routerService.getCurrentState(),
                        authService.getUserAuthentication(),
                        authService.isUserAuthenticated(),
                        sessionService.getCompanyStatus()
                    )
                )
                .subscribe(([isAuthenticated, routerState, userAuth, isUserAuthenticated, companyStatus]: [
                    boolean, IAppRouterState, AuthSession, boolean, any
                ]) => {
                    const isDevSupport: boolean = userAuth?.role === AccountRole.SUPPORT;

                    let validRouteType: RouteType = RouteType.PUBLIC;

                    if (isAuthenticated) {
                        

                        if (isDevSupport) {
                            validRouteType = RouteType.SUPPORT;
                        } else {
                            validRouteType = RouteType.EMPLOYER;
                        }
                    }

                    if (authService.isValidRoute(path, validRouteType)) {
                        resolve(true);

                    } else {
                        if (!isAuthenticated) {

                            if (isDevSupport) {

                                if(isUserAuthenticated) {
                                if (authService.isValidRoute(path, RouteType.SUPPORT)) {
                                        resolve(true);
                                    } else {

                                        store$.dispatch(
                                            new SetRedirectStateAction({
                                                name: toState.name,
                                                params: toState.params
                                            })
                                        );
    
                                        resolve(transition.router.stateService.target(DEVSUPPORT_DASHBOARD));
                                    }
                                } else {

                                    store$.dispatch(
                                        new SetRedirectStateAction({
                                            name: toState.name,
                                            params: toState.params
                                        })
                                    );

                                    const loginPrefs = localStorage.getItem(STORE_LOGIN_PREFS);
                                    const params = { mode: loginPrefs === 'email' ? 'email' : null };
                                    resolve(transition.router.stateService.target(LOGIN, params));
                                }
                                

                            } else if(toState.name !== LOGOUT &&
                                toState.name !== LOGIN &&
                                toState.name !== PASSWORD_RESET &&
                                toState.name !== PASSWORD_VALIDATION_REQUEST &&
                                toState.name !== PASSWORD_VALIDATION_SUBMIT) {
                                    store$.dispatch(
                                        new SetRedirectStateAction({
                                            name: toState.name,
                                            params: toState.params
                                        })
                                    );

                                    const loginPrefs = localStorage.getItem(STORE_LOGIN_PREFS);
                                    const params = { mode: loginPrefs === 'email' ? 'email' : null };
                                    resolve(transition.router.stateService.target(LOGIN, params));
                                }
                        } else if (isAuthenticated && isDevSupport && authService.isValidRoute(path, RouteType.EMPLOYER)) {

                            // assess dev support route when dev support has logged in as company
                            resolve(true);

                        } else {
                            resolve(transition.router.stateService.target(
                                environment.settings.home_employer
                            ));
                        }
                    }
                })

        });
    }

    function extractAnchorWithParams(url: string): string {
        const index: number = url.indexOf('#');

        if (index === -1 || index === url.length - 1) {
            return '/';
        }

        return url.substring(index + 1);
    }

    function removeQuery(url: string): string {
        const index: number = url.indexOf('?');
        return index === -1 ? url : url.substring(0, index);
    }
}